import { Alert, Button } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { FieldValueLookupProvider } from 'features/libra/fieldValues/FieldValueLookupContext'
import { useWorkflowFieldValues } from 'features/libra/fieldValues/useWorkflowFieldValues'
import {
  FieldValues,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useActiveScopeOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { ArrayTableSectionView } from 'features/libra/sections/arrayTableSection/ArrayTableSectionView'
import { isComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import { getFlowchartFieldId } from 'features/libra/sections/flowchartSection/flowchartTypes'
import { Section, SectionType } from 'features/libra/sections/sectionTypes'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'

import {
  ConditionalFormConfig,
  ConditionalFormConfigSchema,
} from './conditionalForm/api/conditionalFormApi'
import { ComputedSectionView } from './conditionalForm/computed/ComputedSectionView'
import { ConditionalFormSectionView } from './conditionalForm/conditionalFormSection/ConditionalFormSection'
import { isConditionalFormStepComplete } from './conditionalForm/conditionalFormStepCompletion'
import { FlowchartSectionView } from './conditionalForm/flowchart/FlowchartSection'
import { AiAutofillBanner } from './GongAgentAutofill'

const AgentSectionBanner: FC<{
  section: { id: string; agentPermissions?: string[] }
  setFieldValues: (
    updates: FieldValues | ((prev: FieldValues) => FieldValues)
  ) => void
}> = ({ section, setFieldValues }) => {
  const onFieldValues = useCallback(
    (fv: FieldValues) => setFieldValues((prev) => ({ ...prev, ...fv })),
    [setFieldValues]
  )
  return <AiAutofillBanner section={section} onFieldValues={onFieldValues} />
}

interface SectionRendererProps {
  section: Section
  stepId: string
  ontologyFields: Record<string, OntologyField>
  fieldValuesWithDefaults: FieldValues
  initialFieldValues: FieldValues
  setFieldValues?: (
    updates: FieldValues | ((prev: FieldValues) => FieldValues)
  ) => void
}

const SectionRenderer: FC<SectionRendererProps> = ({
  section,
  stepId,
  ontologyFields,
  fieldValuesWithDefaults,
  initialFieldValues,
  setFieldValues,
}) => {
  if (isComputedSection(section)) {
    return (
      <ComputedSectionView
        config={section}
        fieldValues={fieldValuesWithDefaults}
        ontologyFields={ontologyFields}
        setFieldValues={setFieldValues}
        context={{
          ontologyFields,
        }}
      />
    )
  }

  switch (section.type) {
    case SectionType.FLOWCHART: {
      if (!setFieldValues) return null
      const fieldId = getFlowchartFieldId(section)
      const fieldDef = ontologyFields[fieldId] as InputField | undefined

      return (
        <CommentIndicator
          anchor={{ type: 'flowchart', stepId, sectionId: section.id }}>
          <FlowchartSectionView
            section={section}
            fieldId={fieldId}
            fieldDef={fieldDef}
            ontologyFields={ontologyFields}
            fieldValue={fieldValuesWithDefaults[fieldId]}
            savedFieldValue={initialFieldValues[fieldId]}
            detailsValue={
              section.detailsId
                ? fieldValuesWithDefaults[section.detailsId]
                : undefined
            }
            setFieldValues={setFieldValues}
          />
        </CommentIndicator>
      )
    }
    case SectionType.FORM:
      return (
        <ConditionalFormSectionView
          config={section}
          ontologyFields={ontologyFields}
          virtualObject={fieldValuesWithDefaults}
          setVirtualObject={setFieldValues}
          stepId={stepId}
        />
      )
    case SectionType.ARRAY_TABLE:
      return (
        <CommentIndicator
          anchor={{ type: 'section', stepId, sectionId: section.id }}>
          <ArrayTableSectionView
            config={section}
            ontologyFields={ontologyFields}
            fieldValues={fieldValuesWithDefaults}
            setFieldValues={setFieldValues}
          />
        </CommentIndicator>
      )
  }
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => ConditionalFormConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<ConditionalFormConfig>
> = ({ config, readonly }) => {
  const stepId = useActiveStepId()
  // Scope-aware ontology lookup: component-scoped defs inside a
  // `<UnitComponentTabs>`, top-level defs outside.
  const { ontologyFields } = useActiveScopeOntologyFields()
  const parsedConfig = useMemo(() => {
    return ConditionalFormConfigSchema.parse(config)
  }, [config])
  const { form, sections, lookups } = parsedConfig

  const {
    fieldValuesWithDefaults,
    initialFieldValues,
    setFieldValues,
    allVisibleSections,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  } = useWorkflowFieldValues({
    ontologyFields,
    sections,
    form,
  })

  const saveGuard = useRegisterSaveGuard(!readonly && hasChanges, handleSave)

  const isStepComplete = useMemo(() => {
    return isConditionalFormStepComplete({
      config: parsedConfig,
      fieldValues: fieldValuesWithDefaults,
    })
  }, [parsedConfig, fieldValuesWithDefaults])

  useReportValidation(
    readonly || isStepComplete
      ? undefined
      : {
          status: 'Error',
          message: 'Step incomplete - please fill in all required fields',
        }
  )

  return (
    <FormContainer>
      <SaveGuardModal {...saveGuard} />
      {!readonly && (
        <StickySaveBannerContainer>
          <StyledAlert
            title={hasChanges ? 'Save Changes?' : 'No Changes'}
            type={hasChanges ? 'warning' : 'success'}
            showIcon
            action={
              hasChanges && (
                <BannerActions>
                  <CancelButton
                    type='dashed'
                    size='small'
                    disabled={isLoading}
                    onClick={handleCancel}>
                    Cancel
                  </CancelButton>
                  <SaveButton
                    type='primary'
                    size='small'
                    disabled={isLoading}
                    onClick={handleSave}>
                    Save
                  </SaveButton>
                </BannerActions>
              )
            }
          />
        </StickySaveBannerContainer>
      )}
      <FieldValueLookupProvider config={lookups}>
        {allVisibleSections.map((section) => {
          return (
            <SectionBlock key={section.id}>
              {section.title && <SectionTitle>{section.title}</SectionTitle>}
              {'agent' in section && section.agent && !readonly && (
                <AgentSectionBanner
                  section={section}
                  setFieldValues={setFieldValues}
                />
              )}
              <SectionRenderer
                section={section}
                stepId={stepId}
                ontologyFields={ontologyFields}
                fieldValuesWithDefaults={fieldValuesWithDefaults}
                initialFieldValues={initialFieldValues}
                setFieldValues={readonly ? undefined : setFieldValues}
              />
            </SectionBlock>
          )
        })}
      </FieldValueLookupProvider>
    </FormContainer>
  )
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-bottom: 1rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.activeText};
`

const StickySaveBannerContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${({ theme }) => theme.forecast.background1};
`

const StyledAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-content {
    display: flex;
    align-items: center;
  }
`

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CancelButton = styled(Button)`
  background-color: ${cantileverTheme.colors.grayscale['500']} !important;
`

const SaveButton = styled(Button)`
  background-color: ${cantileverTheme.colors.OceanBlue} !important;
`
